import {
    Box, Text, Flex, Input, Button,
    InputGroup, InputLeftElement, Spinner,
    useBreakpointValue, VStack
} from "@chakra-ui/react";
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import UsersService from '../../services/users/usersService';
import { useTranslation } from "react-i18next";
import { useAppToast } from "../../hooks/useAppToast";

const UsersListPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("default");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState("");
    const toast = useAppToast();
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Columns configuration for Grid
    const gridTemplateColumns = "80px 1fr 120px 160px 140px";

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await UsersService.getAllUsers({
                page: currentPage,
                search
            });

            // Handle potentially different response structures
            const responseData = res.data?.data || res.data;

            if (responseData?.items) {
                setUsers(responseData.items);
                setTotalPages(responseData.totalPages || 1);
            } else {
                console.warn("Unexpected response structure:", res.data);
                setUsers([]);
            }

        } catch (error) {
            console.error("Users API Error:", error);
            toast({
                title: t('user.error.fetch_list'),
                description: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [currentPage, search]);

    const handleUserClick = (userId) => {
        navigate(`/users/${userId}`);
    };

    return (
        <Flex flexDirection={'column'} w={"100%"} p={8} maxW="7xl" mx="auto">
            <Text fontSize={'2xl'} fontWeight={'bold'} mb={4}>
                {t('user.list.title')}
            </Text>

            {/* Pasek wyszukiwania */}
            <Flex mb={4} gap={4}>
                <InputGroup flex="1">
                    <InputLeftElement>
                        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <path d="M12.0869 12.0869L16 16" stroke="#787878" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7.19565 13.3913C10.6174 13.3913 13.3913 10.6174 13.3913 7.19565C13.3913 3.77389 10.6174 1 7.19565 1C3.77389 1 1 3.77389 1 7.19565C1 10.6174 3.77389 13.3913 7.19565 13.3913Z" stroke="#787878" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </InputLeftElement>
                    <Input
                        placeholder={t('user.list.search_placeholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        bg="white"
                    />
                </InputGroup>
            </Flex>

            {/* Header */}
            {!isMobile && (
                <Box
                    display="grid"
                    gridTemplateColumns={gridTemplateColumns}
                    gap="16px"
                    mb="8px"
                >
                    {['ID', t('table.th.username'), t('table.th.status'), t('table.th.join_date'), t('table.th.events_count')].map((header, index) => (
                        <Box
                            key={index}
                            bg="brand.500"
                            p="12px"
                            display="flex"
                            alignItems="center"
                            fontSize="14px"
                            fontWeight="400"
                            color="white"
                            boxShadow="sm"
                        >
                            {header}
                        </Box>
                    ))}
                </Box>
            )}

            {/* Users List */}
            {loading ? (
                <Flex justify="center" align="center" minH="200px">
                    <Spinner size="xl" />
                </Flex>
            ) : (
                <Box w="100%">
                    {users.length === 0 ? (
                        <VStack align="center" gap={'48px'} p={'48px'}>
                            <Text fontFamily={'Poppins'} fontWeight={'500'} fontSize={'20px'} color="#363636">
                                {t('user.list.empty')}
                            </Text>
                        </VStack>
                    ) : (
                        users.map((user) => (
                            <Box
                                key={user.id}
                                display="grid"
                                gridTemplateColumns={{ base: '1fr', md: gridTemplateColumns }}
                                gap="16px"
                                mb="12px"
                                fontFamily="Inter"
                                fontSize="14px"
                                fontWeight="400"
                            >
                                <Box bg="white" p="12px" display="flex" alignItems="center" onClick={() => handleUserClick(user.id)} cursor="pointer">
                                    <Text isTruncated title={user.id}>{user.id}</Text>
                                </Box>
                                <Box bg="white" p="12px" display="flex" alignItems="center" onClick={() => handleUserClick(user.id)} cursor="pointer">
                                    <Text>{user.fullName || user.email}</Text>
                                </Box>
                                <Box bg="white" p="12px" display="flex" alignItems="center">
                                    <Text>{user.status}</Text>
                                </Box>
                                <Box bg="white" p="12px" display="flex" alignItems="center">
                                    <Text>{new Date(user.joinedDate).toLocaleDateString('pl-PL')}</Text>
                                </Box>
                                <Box bg="white" p="12px" display="flex" alignItems="center">
                                    <Text>{user.eventsCount}</Text>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            )}

            {/* Paginacja */}
            {totalPages > 1 && (
                <Flex justify="center" mt={4} gap={2}>
                    <Button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        isDisabled={currentPage === 1}
                    >
                        {t('pagination.prev')}
                    </Button>
                    <Text alignSelf="center">
                        {t('pagination.page_info', { current: currentPage, total: totalPages })}
                    </Text>
                    <Button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        isDisabled={currentPage === totalPages}
                    >
                        {t('pagination.next')}
                    </Button>
                </Flex>
            )}
        </Flex>
    );
};

export default UsersListPage;
